import { FilterOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Table, Tooltip, Button, Tag, Spin, Divider, Popconfirm, message } from 'antd';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import { TableSearchInput } from 'common/components/table-filter-component';
import { sortDirectionMapping, communicationStatus, sortByMapping } from '~constants';
import { PicEmptyTags } from 'common/components/svgs';
import { mergeSearchParams } from '~helpers/history';
import TableColumnSearch from '~components/table-column-search';
import update from 'immutability-helper';
import * as actions from '~actions/tags/index';
import React from 'react';
import { connect } from 'react-redux';
import { formatDay, dayFormatType } from '~helpers/day';
import OperateTagModal from './modal';

const mapStateToProps = state => ({
  list: state.dashboard.tags.get('list')?.toJS(),
  communication: state.dashboard.tags.get('communication')?.toJS(),
});

const mapDispatchToProps = dispatch => ({
  getTagsList: (filters, callback = () => {}) => {
    dispatch(actions.getTagsList(filters, callback));
  },
  deleteTag: (id, callback = () => {}) => {
    dispatch(actions.deleteTag({ id: id }, callback));
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withRouter
@withTranslation()
export default class Tags extends React.Component {
  constructor(props) {
    super(props);
    this.defaultSearchParams = {
      pageNumber: 1,
      pageSize: 10,
      sortBy: null,
      sortDirection: null,
      propertyId: null,
      name: null,
      status: null,
      updatedAt: null,
    };
    this.state = {
      filters: this.formatFilters(queryString.parse(props.location.search)),
      selectedFilterPropertyName: null,
      selectedFilterPropertyId: null,
      showEditModal: false,
      totalNumber: null,
      selectedTag: null,
      visibility: {
        propertyFilter: false,
        name: false,
        status: false,
      },
    };
    this.columnInputs = {
      propertyFilter: null,
    };
  }

  fetchTagsList = () => {
    this.props.getTagsList(this.state.filters || {}, res => {
      this.setState({
        totalNumber: res?.tags?.totalCount,
      });
    });
  };

  componentDidMount() {
    this.fetchTagsList();
  }

  componentWillReceiveProps(nextProps) {
    if (JSON.stringify(nextProps.location.search) !== JSON.stringify(this.props.location.search)) {
      const filters = this.formatFilters(queryString.parse(nextProps.location.search));
      this.closeDropDownVisible();
      this.props.getTagsList(filters || {});
      this.setState({
        filters: update(this.state.filters, { $set: filters }),
      });
    }
  }

  operateTag = (record = null) => {
    this.setState({
      selectedTag: record,
      showEditModal: true,
    });
  };

  handleDeleteTag = id => {
    this.props.deleteTag(id, () => {
      message.success('Delete Tag success');
      this.fetchTagsList();
    });
  };

  handleColumnFilter = (type, value) => {
    this.pushState({
      [type]: value,
      pageNumber: 1,
    });
  };

  closeDropDownVisible = () => {
    this.setState({
      visibility: {
        propertyFilter: false,
      },
    });
  };

  handleTableChange = (pagination, filters, sorter) => {
    const params = {
      pageNumber: pagination.current,
      sortBy: sortByMapping[sorter.columnKey] || null,
      sortDirection: sortDirectionMapping[sorter.order] || null,
      status: filters.status || null,
    };

    if (pagination.pageSize !== this.state.filters.pageSize) {
      params.pageSize = pagination.pageSize;
      params.pageNumber = 1;
    }
    this.pushState(params);
  };

  pushState = data => {
    this.props.history.push(mergeSearchParams(data, this.defaultSearchParams));
  };

  formatData = () => {
    let data = this.props.list.payload;
    if (data.length === 0) {
      return data;
    }
    return data;
  };

  formatFilters = filters => ({
    pageNumber: Number(filters.pageNumber) || this.defaultSearchParams.pageNumber,
    pageSize: Number(filters.pageSize) || this.defaultSearchParams.pageSize,
    sortBy: filters.sortBy || null,
    sortDirection: filters.sortDirection || null,
    propertyId: filters.propertyId || null,
    name: filters.name || null,
    status: filters.status || null,
    updatedAt: filters.updatedAt || null,
  });

  showTableColumns = () => {
    const columns = this.tableColumns();
    return columns.filter(item => item.key !== 'title' && item.key !== 'description');
  };

  getApplyLevelTagColor = applyLevel => {
    switch (applyLevel) {
      case 'property':
        return 'green';
      case 'landlord':
        return 'blue';
      case 'city':
        return 'gold';
    }
  };

  tableColumns = () => [
    {
      title: this.props.t('cms.tag.form.tag_name'),
      dataIndex: 'name',
      key: 'name',
      width: '17%',
      className: 'tags__table-text',
      filterDropdown: (
        <div className="tags__table-filter">
          <TableSearchInput
            field="name"
            value={this.state.filters.name || ''}
            onChange={(_fieldName, value) => {
              this.setState(
                {
                  visibility: update(this.state.visibility, { name: { $set: false } }),
                },
                () => {
                  this.handleColumnFilter('name', value);
                },
              );
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined style={{ color: this.state.filters.name ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: this.state.visibility.name,
      onFilterDropdownVisibleChange: view => {
        this.setState({
          visibility: update(this.state.visibility, { name: { $set: view } }),
        });
      },
      render: text => (
        <Tooltip placement="bottomLeft" title={text}>
          <span>{text || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: this.props.t('cms.tag.form.apply_level'),
      dataIndex: 'applyLevel',
      key: 'applyLevel',
      width: '13%',
      className: 'tags__table-text',
      render: (text, record) => (
        <div className="tags__table-text--level">
          <For each="level" of={record.applyLevel}>
            <Tag key={level} color={this.getApplyLevelTagColor(level)}>
              {level} level
            </Tag>
          </For>
        </div>
      ),
    },
    {
      title: this.props.t('cms.tag.form.start_time'),
      dataIndex: 'startDate',
      key: 'startDate',
      className: 'tags__table-text',
      width: '12%',
      sorter: true,
      sortOrder:
        (this.state.filters.sortBy === sortByMapping.startDate &&
          sortDirectionMapping[this.state.filters.sortDirection]) ||
        false,
      render: text => <span>{text ? formatDay(dayFormatType.UKFORMAT, text) : '-'}</span>,
    },
    {
      title: this.props.t('cms.tag.form.end_time'),
      dataIndex: 'endDate',
      width: '12%',
      key: 'endDate',
      className: 'tags__table-text',
      sorter: true,
      sortOrder:
        (this.state.filters.sortBy === sortByMapping.endDate &&
          sortDirectionMapping[this.state.filters.sortDirection]) ||
        false,
      render: text => <span>{text ? formatDay(dayFormatType.UKFORMAT, text) : '-'}</span>,
    },
    {
      title: this.props.t('cms.tag.form.status'),
      dataIndex: 'status',
      width: '11%',
      key: 'status',
      filters: [
        { text: this.props.t('cms.tag.form.status.ACTIVE'), value: 'ACTIVE' },
        { text: this.props.t('cms.tag.form.status.SCHEDULED'), value: 'SCHEDULED' },
        { text: this.props.t('cms.tag.form.status.EXPIRED'), value: 'EXPIRED' },
      ],
      filterDropdownVisible: this.state.visibility.status,
      onFilterDropdownVisibleChange: view => {
        this.setState({
          visibility: update(this.state.visibility, { status: { $set: view } }),
        });
      },
      filterIcon: (
        <FilterOutlined style={{ color: this.state.filters.status ? '#38b2a6' : '#c8c9cb' }} />
      ),
      render: text => {
        return (
          <span
            className={classNames('tags__table-status-text', {
              'tags__table-status-text--active': text === 'ACTIVE',
              'tags__table-status-text--scheduled': text === 'SCHEDULED',
              'tags__table-status-text--expired': text === 'EXPIRED',
            })}
          >
            {this.props.t(`cms.tag.form.status.${text}`)}
          </span>
        );
      },
    },
    {
      title: this.props.t('cms.tag.form.updated_at'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '13%',
      sorter: true,
      sortOrder:
        (this.state.filters.sortBy === sortByMapping.updatedAt &&
          sortDirectionMapping[this.state.filters.sortDirection]) ||
        false,
      render: text => {
        return <span>{text ? formatDay(dayFormatType.UKWITHHM, text) : '-'}</span>;
      },
    },
    {
      title: this.props.t('cms.tag.form.edit_by'),
      dataIndex: 'updatedUser',
      key: 'updatedUser',
      width: '12%',
      render: user => {
        return <span>{`${user.firstName} ${user.lastName}`}</span>;
      },
    },
    {
      title: this.props.t('cms.tag.column.actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: '10%',
      render: (_text, _record) => (
        <div>
          <a role="button" tabIndex="0" onClick={() => this.operateTag(_record)}>
            {this.props.t('cms.tag.column.actions.edit')}
          </a>
          <Divider type="vertical" />
          <Popconfirm
            placement="bottomRight"
            title={
              <p className="tags__table__delete-popover-text">
                {this.props.t('cms.tag.column.actions.delete.tooltip')}
              </p>
            }
            onConfirm={() => this.handleDeleteTag(_record.id)}
            okText={this.props.t('cms.tag_modal.header.btn.confirm')}
            cancelText={this.props.t('cms.tag_modal.header.btn.cancel')}
          >
            <a role="button" tabIndex="1" className="tags__table-delete">
              {this.props.t('cms.tag.column.actions.delete')}
            </a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  renderEmptyPage = () => {
    return (
      <div className="tags__empty-wrapper">
        <PicEmptyTags className="tags__empty-page-icon" />
        <span className="tags__empty-summary">
          {this.props.t('cms.tag.empty_page.havent_added_tag')}
        </span>
        <Button onClick={() => this.operateTag()} className="tags__empty-btn">
          <PlusOutlined />
          {this.props.t('cms.table.header.button.add_new_tag')}
        </Button>
      </div>
    );
  };

  handleModalClose = () => {
    this.setState({ showEditModal: false });
  };

  handleSuccess = () => {
    this.props.getTagsList(this.state.filters || {}, res => {
      this.setState({
        totalNumber: res?.tags?.totalCount,
      });
    });
  };

  render() {
    return (
      <div>
        <div className="table-header">
          <div className="tags__header-container">
            <h1 className="table-header__heading">{this.props.t('cms.table.header.text.tags')}</h1>

            <div className="table-header__button-container">
              <Button onClick={() => this.operateTag()}>
                <PlusOutlined />
                {this.props.t('cms.table.header.button.add_new_tag')}
              </Button>
            </div>
          </div>
        </div>
        <Choose>
          <When
            condition={
              this.props.communication.list.status === communicationStatus.FETCHING &&
              this.state.totalNumber === null
            }
          >
            <Spin className="tags__loading" />
          </When>
          <When
            condition={
              (!this.state.totalNumber || this.state.totalNumber === 0) &&
              JSON.stringify(this.state.filters) === JSON.stringify(this.defaultSearchParams)
            }
          >
            {' '}
            {this.renderEmptyPage()}
          </When>
          <Otherwise>
            {' '}
            <div className="tags__table-filter-container">
              <div className="tags__table-filter-container__item tags__table-filter-container__property">
                <span>{this.props.t('cms.search.title.property')}:</span>
                <TableColumnSearch
                  searchType="property"
                  valueData={this.state.selectedFilterPropertyName || ''}
                  placeholder={this.props.t('cms.table.column.search.property_name')}
                  isLocationCustom
                  onSearch={(_, option) => {
                    this.setState({
                      selectedFilterPropertyName: option.name,
                      selectedFilterPropertyId: option.id,
                    });
                  }}
                  t={this.props.t}
                />
              </div>
              <div className="tags__table-filter-container__control">
                <Button
                  className="tags__table-filter-container__reset-button"
                  onClick={() => {
                    this.setState({
                      selectedFilterPropertyName: null,
                      selectedFilterPropertyId: null,
                    });
                    this.handleColumnFilter('propertyId', null);
                  }}
                >
                  {this.props.t('cms.tag.header.btn.reset')}
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    this.handleColumnFilter('propertyId', this.state.selectedFilterPropertyId);
                  }}
                >
                  {this.props.t('cms.tag.header.btn.search')}
                </Button>
              </div>
            </div>
            <div className="tags__table-list__wrapper">
              <Table
                columns={this.showTableColumns()}
                rowKey="id"
                className="table-list"
                dataSource={this.formatData()}
                pagination={{
                  current: this.state.filters.pageNumber,
                  pageSize: this.state.filters.pageSize,
                  showSizeChanger: true,
                  total: this.props.list.total,
                }}
                loading={this.props.communication.list.status === communicationStatus.FETCHING}
                onChange={this.handleTableChange}
              />
            </div>
          </Otherwise>
        </Choose>

        <OperateTagModal
          visible={this.state.showEditModal}
          onClose={this.handleModalClose}
          onSuccess={this.handleSuccess}
          record={this.state.selectedTag}
          t={this.props.t}
        />
      </div>
    );
  }
}

Tags.propTypes = {
  t: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }),
  communication: PropTypes.shape({
    create: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    update: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    delete: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    list: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  list: PropTypes.shape({
    payload: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
  }),
  getOfferList: PropTypes.func.isRequired,
  deleteOffer: PropTypes.func.isRequired,
  setOperatingOffer: PropTypes.func.isRequired,
};

Tags.defaultProps = {
  t: () => {},
  initialize: () => {},
  setOperatingOffer: () => {},
  communication: {
    create: {
      status: '',
    },
    update: {
      status: '',
    },
    delete: {
      status: '',
    },
    list: {
      status: '',
    },
  },
  location: {
    search: '',
  },
  history: {
    push: () => {},
  },
  list: {
    payload: [],
    total: 0,
  },
  deleteOffer: () => {},
  getOfferList: () => {},
};
